import React, {useEffect, useRef, useCallback, useState} from 'react';
import {useIntl} from 'react-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentContainer from '../../../components/PaymentContainer/PaymentContainer';
import PaymentSummary from '../../../components/PaymentSummary/PaymentSummary';
import RenewalHelpCard from '../components/RenewalHelpCard';
import DisclaimerNote from '../components/DisclaimerNote';
import {titleAccentDot} from '../utils/titleAccentDot';
import {useRenewalClick} from '../context/RenewalClickContext';
import {SCREENS, COVER_TYPES, RenewalJourneyEvent, RenewalJourneyLocation} from '../constants/renewalClick';
import {submitRenewalPayment, fireRenewalJourneyActivity} from '../api/renewalClickApi';

const styles = {
  container: {px: 2, pb: 5, maxWidth: 560, mx: 'auto'},
  title: (theme) => ({
    fontSize: theme.typography.pxToRem(22),
    fontWeight: 700,
    color: 'secondary.main',
    mb: 0.5,
    textAlign: 'center',
    '&::after': titleAccentDot,
  }),
  paymentWrapper: {
    width: '100%',
    maxWidth: {xs: '100%', sm: 480},
    mx: 'auto',
    '& [class*="purchaseContainer"]': {
      padding: 0,
      marginTop: 0,
    },
    // Force single-column layout in our narrow container
    '& .MuiGrid-container > .MuiGrid-root': {
      flexBasis: '100%',
      maxWidth: '100%',
    },
    '& [class*="purchaseBtnWrapper"]': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  backToOffer: (theme) => ({
    display: 'block',
    mx: 'auto',
    mt: 2,
    mb: 2,
    p: 0,
    border: 0,
    background: 'none',
    color: theme.palette.secondary.main,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  }),
  securePayment: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
    mt: 1.25,
    fontSize: theme.typography.pxToRem(12),
    color: 'text.secondary',
  }),
};

const PaymentScreen = () => {
  const intl = useIntl();
  const m = (id, values) => intl.formatMessage({id}, values);
  const {
    shortLinkKey, opportunityId, policy, pricing, paymentRestrictions,
    paymentProcessing, paymentError,
    setPaymentProcessing, setPaymentError, setScreen,
  } = useRenewalClick();
  const hasTracked = useRef(false);
  // the payment form starts at 1 payment for each product category group
  const [payments, setPayments] = useState({casco: 1, mandatory: 1});
  const handlePaymentsChange = useCallback((productCategoryGroup, value) => {
    setPayments((current) => ({...current, [productCategoryGroup]: value}));
  }, []);
  const policyType = policy.coverType === COVER_TYPES.THIRD_PARTY
    ? m('renewalClick.details.thirdParty')
    : m('renewalClick.details.comprehensive');

  useEffect(() => {
    if (!hasTracked.current && opportunityId) {
      hasTracked.current = true;
      fireRenewalJourneyActivity(opportunityId, RenewalJourneyEvent.REACHED_PAYMENT, {location: RenewalJourneyLocation.PAYMENT});
    }
  }, [opportunityId]);

  const onPaymentSubmit = useCallback(async (values, {setErrors}) => {
    setPaymentProcessing(true);
    setPaymentError(null);

    const paymentData = {
      ...values,
      month: String(values.month).padStart(2, '0'),
      year: String(values.year),
      // the payments dropdowns give numbers, salesforce-int expects strings
      paymentCasco: String(values.paymentCasco),
      paymentMandatory: String(values.paymentMandatory),
    };

    try {
      const response = await submitRenewalPayment(shortLinkKey, paymentData);
      if (response.isSuccess) {
        fireRenewalJourneyActivity(opportunityId, RenewalJourneyEvent.PAYMENT_COMPLETED, {location: RenewalJourneyLocation.PAYMENT});
        setScreen(SCREENS.SIGNING_PENDING);
      } else {
        const errorMsg = response.friendlyError
          ? intl.formatMessage({id: response.friendlyError})
          : intl.formatMessage({id: 'renewalClick.payment.error'});
        setErrors({creditCardNumber: errorMsg});
        setPaymentError(errorMsg);
      }
    } catch {
      const msg = intl.formatMessage({id: 'renewalClick.payment.error'});
      setErrors({creditCardNumber: msg});
      setPaymentError(msg);
    } finally {
      setPaymentProcessing(false);
    }
  }, [shortLinkKey, intl, setPaymentProcessing, setPaymentError, setScreen, opportunityId]);

  const handleBackToOffer = useCallback(() => {
    setScreen(SCREENS.OFFER);
  }, [setScreen]);

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>
        {m('renewalClick.payment.title')}
      </Typography>

      {paymentError && (
        <Alert severity='error' sx={{mb: 2}}>{paymentError}</Alert>
      )}

      <Box sx={styles.paymentWrapper}>
        <PaymentContainer
          onPaymentSubmit={onPaymentSubmit}
          isVerifyingPayment={paymentProcessing}
          isUseCardForBoth
          isFirstCard
          policyType={policyType}
          isAsset={false}
          displayUserOwnCardFields
          paymentRestrictions={paymentRestrictions}
          onPaymentsChange={handlePaymentsChange}
        >
          <Box sx={{width: '100%'}}>
            <PaymentSummary
              isThirdParty={policy.coverType === COVER_TYPES.THIRD_PARTY}
              cascoPrice={pricing.cascoPremium}
              mandatoryPrice={pricing.mandatoryPremium}
              serviceFee={pricing.serviceFee}
              otherProducts={(pricing.otherProducts || []).map(({name, amount}) => ({name, price: amount}))}
              restrictions={paymentRestrictions || {}}
              payments={payments} />
          </Box>
        </PaymentContainer>
      </Box>

      {/* Bottom info row */}
      <Typography sx={styles.securePayment}>
        <LockOutlinedIcon sx={{fontSize: 14}} />
        {m('renewalClick.securePayment')}
      </Typography>

      {/* Cancellation note — same visual pattern as OfferScreen's disclaimer */}
      <DisclaimerNote
        icon={
          <Tooltip title={m('renewalClick.offer.serviceFeeDisclaimer')} arrow>
            <InfoOutlinedIcon sx={{color: 'primary.main', fontSize: 22, flexShrink: 0, mt: 0.25, cursor: 'pointer'}} />
          </Tooltip>
        }
        title={m('renewalClick.payment.cancellationTitle')}
      >
        {m('renewalClick.payment.cancellationText')}
      </DisclaimerNote>

      {/* Back to offer */}
      <Typography
        component='button'
        type='button'
        onClick={() => setScreen(SCREENS.OFFER)}
        sx={styles.backToOffer}
      >
        {m('renewalClick.payment.backToOffer')}
      </Typography>

      {/* Help card */}
      <Box sx={{mb: 2.5}}>
        <RenewalHelpCard
          title={m('renewalClick.helpTitle')}
          description={m('renewalClick.helpDescription')}
        />
      </Box>

    </Box>
  );
};

export default PaymentScreen;
